import React from 'react';
import styled from 'styled-components';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Code, TrendingUp, Target, CheckCircle, Shield } from 'lucide-react';

// Language detection helper
const detectLanguage = (text) => {
  if (!text) return 'english';
  
  // Check for Arabic characters
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  
  if (arabicRegex.test(text)) {
    return 'arabic';
  }
  
  return 'english';
};

const SQLSection = styled.div`
  margin: 16px 0;
  padding: 0;

  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
    direction: rtl;
    text-align: right;

    svg {
      width: 18px;
      height: 18px;
      color: #6366f1;
    }
  }

  .content {
    background: #1a1b26;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    /* Auto-resize height, prevent horizontal scroll */
    height: auto;
    min-height: auto;
    overflow-x: hidden;
    overflow-y: visible;
    
    .syntax-highlighter {
      margin: 0 !important;
      background: transparent !important;
      padding: 16px !important;
      font-size: 14px;
      line-height: 1.5;
      
      /* Ensure proper text wrapping */
      pre {
        white-space: pre-wrap !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        overflow: visible !important;
      }
      
      code {
        white-space: pre-wrap !important;
        word-wrap: break-word !important;
      }
    }
  }

  .content-text {
    background: #f8fafc;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    color: #374151;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-size: 14px;
  }
`;

const GenerationSection = styled.div`
  margin: 16px 0;

  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
    direction: rtl;
    text-align: right;

    svg {
      width: 18px;
      height: 18px;
      color: #10b981;
    }
  }

  .content-text {
    background: #f0fdf4;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #bbf7d0;
    color: #166534;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-size: 14px;
    direction: auto;
    text-align: start;
    unicode-bidi: plaintext;
  }

  .content-text.arabic {
    direction: rtl;
    text-align: right;
  }

  .content-text.english {
    direction: ltr;
    text-align: left;
  }
`;

const SQLExplanationSection = styled.div`
  margin: 16px 0;

  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
    direction: rtl;
    text-align: right;

    svg {
      width: 18px;
      height: 18px;
      color: #6366f1;
    }
  }

  .content-text {
    background: #eff6ff;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #bfdbfe;
    color: #1e40af;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-size: 14px;
    direction: rtl;
    text-align: right;
  }
`;

const SQLDisplay = ({ message }) => {
  return (
    <>
      {/* SQL Query */}
      {message.sql_query && (
        <SQLSection>
          <div className="label">
            <Code />
            استعلام SQL:
          </div>
          <div className="content">
            <SyntaxHighlighter
              language="sql"
              style={tomorrow}
              className="syntax-highlighter"
              customStyle={{
                margin: '0',
                padding: '16px',
                fontSize: '14px',
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '100%',
                overflow: 'visible'
              }}
              wrapLongLines={true}
              PreTag="div"
            >
              {message.sql_query}
            </SyntaxHighlighter>
          </div>
        </SQLSection>
      )}

      {/* Final Answer - Parse and separate from SQL explanation */}
      {message.final_answer && (() => {
        // Split final_answer by "شرح إستعلام SQL" separator
        const sqlExplanationMarker = 'شرح إستعلام SQL';
        const parts = message.final_answer.split(sqlExplanationMarker);

        const actualAnswer = parts[0].trim();
        const sqlExplanation = parts.length > 1 ? parts[1].trim() : null;

        return (
          <>
            {/* The actual answer in a green box */}
            {actualAnswer && (
              <GenerationSection>
                <div className="label">
                  <TrendingUp />
                  الإجابة النهائية:
                </div>
                <div className={`content-text ${detectLanguage(actualAnswer)}`}>
                  {actualAnswer}
                </div>
              </GenerationSection>
            )}

            {/* SQL Explanation in a separate blue/info box */}
            {sqlExplanation && (
              <SQLExplanationSection>
                <div className="label">
                  <Code />
                  شرح إستعلام SQL
                </div>
                <div className="content-text">
                  {sqlExplanation}
                </div>
              </SQLExplanationSection>
            )}
          </>
        );
      })()}

      {/* Answer Synthesis */}
      {message.answer_synthesis && (
        <GenerationSection>
          <div className="label">
            <Target />
            تركيب الإجابة:
          </div>
          <div className={`content-text ${detectLanguage(message.answer_synthesis)}`}>
            {message.answer_synthesis}
          </div>
        </GenerationSection>
      )}

      {/* Generation Details */}
      {message.generation_details && (
        <GenerationSection>
          <div className="label">
            <CheckCircle />
            تفاصيل التوليد:
          </div>
          <div className={`content-text ${detectLanguage(message.generation_details)}`}>
            {message.generation_details}
          </div>
        </GenerationSection>
      )}

      {/* Reasoning */}
      {message.reasoning && (
        <GenerationSection>
          <div className="label">
            <Shield />
            المنطق:
          </div>
          <div className={`content-text ${detectLanguage(message.reasoning)}`}>
            {message.reasoning}
          </div>
        </GenerationSection>
      )}

      {/* Steps */}
      {message.steps && (
        <GenerationSection>
          <div className="label">
            <CheckCircle />
            الخطوات:
          </div>
          <div className={`content-text ${detectLanguage(Array.isArray(message.steps) ? message.steps.join('\n') : message.steps)}`}>
            {Array.isArray(message.steps) ? message.steps.join('\n') : message.steps}
          </div>
        </GenerationSection>
      )}

      {/* Process */}
      {message.process && (
        <GenerationSection>
          <div className="label">
            <Shield />
            العملية:
          </div>
          <div className={`content-text ${detectLanguage(message.process)}`}>
            {message.process}
          </div>
        </GenerationSection>
      )}
    </>
  );
};

export default SQLDisplay;